import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/common/DatePicker';
import { useState } from 'react';
import { Summary } from './Summary';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import styles from './style.module.css';
import {
  ALL_TIME_SLOTS,
  REPEAT_OPTIONS,
} from '@/pages/DoctorManagementPage/constants';

interface AddUnavailabilityProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit?: (formData: FormData) => void;
  showRepeatOption: boolean;
}

export function AddUnavailability({
  open,
  onOpenChange,
  showRepeatOption,
  onSubmit,
}: AddUnavailabilityProps) {
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>();
  const [startTime, setStartTime] = useState<string | null>(null);
  const [endTime, setEndTime] = useState<string | null>(null);
  const [repeatOption, setRepeatOption] = useState('does_not_repeat');
  const [description, setDescription] = useState('');
  const [showSummary, setShowSummary] = useState(false);
  const [formData, setFormData] = useState<FormData | null>(null);

  const resetForm = () => {
    setStartDate(null);
    setEndDate(null);
    setStartTime(null);
    setEndTime(null);
    setTitle('');
    setDescription('');
    setRepeatOption('Does not repeat');
  };

  const handleSubmit = () => {
    const data = new FormData();
    data.append('title', title);
    data.append('startDate', startDate?.toISOString() || '');
    data.append('endDate', endDate?.toISOString() || '');
    data.append('startTime', startTime || '');
    data.append('endTime', endTime || '');
    data.append('repeatOption', repeatOption);
    data.append('description', description);

    setFormData(data);
    resetForm();
    onOpenChange(false);
    setShowSummary(true);

    if (onSubmit) {
      onSubmit(data);
    }
  };

  const handleStartTimeChange = (selectedOption: string | null) => {
    setStartTime(selectedOption);
    if (selectedOption) {
      const newEndTime = calculateEndTime(selectedOption);
      setEndTime(newEndTime);
    }
  };

  const calculateEndTime = (startTime: string) => {
    const [hours, minutes, period] = startTime
      .match(/(\d+):(\d+)\s(AM|PM)/)!
      .slice(1);
    let newHours = parseInt(hours);
    let newMinutes = parseInt(minutes) + 30;

    if (newMinutes >= 60) {
      newMinutes -= 60;
      newHours += 1;
    }

    if (newHours === 12) {
      return `12:${newMinutes.toString().padStart(2, '0')} ${
        period === 'AM' ? 'PM' : 'AM'
      }`;
    } else if (newHours > 12) {
      newHours -= 12;
      return `${newHours}:${newMinutes.toString().padStart(2, '0')} PM`;
    }

    return `${newHours}:${newMinutes.toString().padStart(2, '0')} ${period}`;
  };

  const filterEndTimeOptions = (startTime: string | null) => {
    if (!startTime) return [];

    const startIndex = ALL_TIME_SLOTS.indexOf(startTime);
    return ALL_TIME_SLOTS.slice(startIndex + 1).map((time, index) => (
      <SelectItem value={time} key={`select-dropdown-${time}-${index}`}>
        {time}
      </SelectItem>
    ));
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            resetForm();
          }
          onOpenChange(isOpen);
        }}
        data-testid="add-unavailability-popup"
      >
        <DialogContent
          aria-describedby="unavailability-description"
          data-testid="dialog-content"
        >
          <DialogTitle className="text-xl font-bold mb-4">
            Add Unavailability
          </DialogTitle>
          <DialogClose asChild />

          <p id="unavailability-description" className="sr-only">
            Please fill out the form below to add your unavailability.
          </p>

          <Input
            type="text"
            placeholder="Enter a title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <div className="flex gap-2.5 w-full">
            <DatePicker
              className="w-full"
              selectedDate={startDate!}
              onSelectDate={setStartDate}
              dateFormat="dd/MM/yyyy"
              placeholder="Enter start date"
              buttonText="Enter start date"
              data-testid="start-date-picker"
            />
            <DatePicker
              className="w-full"
              selectedDate={endDate!}
              onSelectDate={setEndDate}
              dateFormat="dd/MM/yyyy"
              placeholder="Enter end date"
              buttonText="Enter end date"
              data-testid="end-date-picker"
            />
          </div>

          <div className="flex gap-2.5 items-center w-full">
            <Select onValueChange={handleStartTimeChange}>
              <SelectTrigger
                className={`${styles['manage-booking-dropdown-outline']} w-[112px]`}
              >
                <SelectValue placeholder="Start time" />
              </SelectTrigger>
              <SelectContent className="z-[9999]">
                {ALL_TIME_SLOTS.map((time, index) => (
                  <SelectItem
                    value={time}
                    key={`select-dropdown-${time}-${index}`}
                  >
                    {time}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select>
              <SelectTrigger
                className={`${styles['manage-booking-dropdown-outline']} w-[112px]`}
              >
                <SelectValue placeholder="End time" />
              </SelectTrigger>
              <SelectContent className="z-[9999]">
                {filterEndTimeOptions(startTime)}
              </SelectContent>
            </Select>
          </div>

          {showRepeatOption && (
            <Select>
              <SelectTrigger
                className={`${styles['manage-booking-dropdown-outline']} w-[250px]`}
              >
                <SelectValue placeholder="Repeat" />
              </SelectTrigger>
              <SelectContent className="z-[9999]">
                {REPEAT_OPTIONS.map(({ value, label }, index) => (
                  <SelectItem
                    onChange={() => setRepeatOption(value)}
                    value={value}
                    key={`select-dropdown-${value}-${index}`}
                  >
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <textarea
            placeholder="Enter a description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full h-20 border box-border p-2 rounded-[5px] border-solid border-[#ccc]"
          />

          <div className="flex justify-end">
            <Button onClick={handleSubmit} variant="black">
              Submit
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {showSummary && formData && (
        <Summary
          open={showSummary}
          onOpenChange={setShowSummary}
          formData={formData}
        />
      )}
    </>
  );
}
